import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import TopicCard from "@/components/base/TopicCard";

const topicsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "专题合集目录",
    url: "https://jingmeilove.cn/topics",
    description: "按主题整理的热点合集与盘点目录。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://jingmeilove.cn/" },
      { "@type": "ListItem", position: 2, name: "专题合集", item: "https://jingmeilove.cn/topics" },
    ],
  },
];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const keyword = searchParams.get("q") ?? "";

  usePageMeta({
    title: "专题合集目录｜独家吃瓜合集与精选专题 - 瓜集社",
    description:
      "按主题浏览独家吃瓜合集与独家吃瓜精选专题，支持分类筛选与关键词搜索，快速找到想看的独家吃瓜盘点与合集目录。",
    keywords: "独家吃瓜合集,独家吃瓜精选,独家吃瓜盘点,专题目录,合集分类",
    path: "/topics",
    jsonLd: topicsJsonLd,
  });

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return topics.filter((topic) => {
      const matchCategory = activeCategory === "all" || topic.categoryId === activeCategory;
      const matchKeyword =
        !q ||
        topic.title.toLowerCase().includes(q) ||
        topic.summary.toLowerCase().includes(q) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const activeCategoryName =
    activeCategory === "all"
      ? "全部主题"
      : categories.find((c) => c.id === activeCategory)?.name ?? "全部主题";

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">专题合集</span>
          </nav>
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            专题合集目录
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里收录了站内所有的独家吃瓜合集与独家吃瓜精选专题。先选主题，再按关键词搜索，
            就能找到对应的盘点与目录。
          </p>
          <p className="mt-4 text-xs text-foreground-500">
            共 {topics.length} 条合集 · 当前显示 {filtered.length} 条
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParams({ category: "all" })}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeCategory === "all"
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              全部主题
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => updateParams({ category: cat.id })}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400 lg:w-72">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => updateParams({ q: e.target.value })}
              type="search"
              placeholder="搜索合集标题或标签"
              aria-label="搜索合集"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-filter-3-line text-sm" />
          当前筛选：{activeCategoryName}
          {keyword && ` · 关键词「${keyword}」`}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-600">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">
              没有找到匹配的合集
            </p>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或者选择其他主题分类试试。</p>
            <button
              type="button"
              onClick={() => updateParams({ category: "all", q: "" })}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>
    </>
  );
}